import React, { useCallback, useState } from 'react'
import {
  Dropdown,
  Button,
  Modal,
  Input,
  message,
  Checkbox,
  Row,
  Col,
  Divider
} from 'antd'
import { v4 as uuid } from 'uuid'
import produce from 'immer'
import sensors from '../../../../utils/sensors'
import {
  draftWatermarksAction,
  updateOrNewWatermark
} from '../../../../actions/watermark'
import * as WM from '../../typings'
import { setStorage } from '../../lib/draft'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import CONFIG from '../../../../constants/config'

const Search = Input.Search

const CopyButton: React.FC<WM.CopyButtonProps> = ({ watermark }) => {
  const disabled = !watermark.watermarkContent
  const [copyModalVisible, setCopyModalVisible] = useState<boolean>(false)
  const [copyToOtherVisible, setCopyToOtherVisible] = useState<boolean>(false)
  const [copyToGroupsModalLoading, setCopyToGroupsModalLoading] = useState<
    boolean
  >(false)
  const [isSubmit, setIsSubmit] = useState<boolean>(false)
  const [targetGroups, setTargetGroups] = useState<string[]>([])
  const [searchGroupName, setSearchGroupName] = useState<string>('')
  const [name, setName] = useState<string | undefined>('')
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.main.user)
  const groupID = useAppSelector((state) => state.group.groupId)
  const allowCopyGroupIds = (user?.groups || [])
    .filter(
      (group: any) =>
        group.group_id !== groupID &&
        group.role_type > CONFIG.memberRoleTypes.NORMAL
    )
    .map((group: any) => group.group_id)
  const isGroupSelectedAll = allowCopyGroupIds.length === targetGroups.length

  const fetchDraftWatermarks = useCallback(() => {
    dispatch(draftWatermarksAction(groupID))
  }, [groupID])

  const onMenuClick = useCallback(({ item, key, keyPath, domEvent }) => {
    domEvent.stopPropagation()
    switch (key) {
      case '1':
        sensors.track('web_workgroup_watermark_manage_page_click', {
          clickItem: 'copyToTheGroup'
        })
        setCopyModalVisible(true)
        break
      case '2':
        sensors.track('web_workgroup_watermark_manage_page_click', {
          clickItem: 'copyToOtherGroup'
        })
        setCopyToOtherVisible(true)
        break
      default:
        return null
    }
  }, [])

  const onCopy = () => {
    if (name && watermark.watermarkContent) {
      const content = produce(watermark.watermarkContent, (draft) => {
        if (!draft) return
        let createTime = String(new Date().getTime())
        draft.id = uuid()
        draft.createTime = createTime
        draft.update_time = createTime
        draft.name = name
      })
      if (!content) return
      const newWM = produce(watermark, (draft) => {
        draft.content = JSON.stringify(content)
        draft.watermarkContent = content
        draft.id = content.id
        draft.updateTime = ''
        draft.createTime = content.createTime
        draft.isDraft = true
        draft.itemsList = watermark.itemsList.map((item) => {
          item.watermarkID = content.id
          return item
        })
      })

      setStorage(newWM, groupID)
      setCopyModalVisible(false)
      fetchDraftWatermarks()
    }
  }

  const onCopyToOtherGroups = async () => {
    if (!watermark.watermarkContent) return
    setCopyToGroupsModalLoading(true)
    for (let index = 0; index < targetGroups.length; index++) {
      const groupId = targetGroups[index]
      const time = String(new Date().getTime())
      const content = produce(watermark.watermarkContent, (draft) => {
        if (!draft) return
        draft.id = uuid()
        draft.groupId = groupId
        draft.createTime = time
        draft.update_time = time
      })
      if (!content) continue
      if (isSubmit) {
        const usedItems = content.items
          .filter((item) => item.switchStatus)
          .map((item) => item.id)
        if (content.logo?.switchStatus) {
          usedItems.push(content.logo.id)
        }
        if (content.theme?.switchStatus) {
          usedItems.push(content.theme.id)
        }
        await dispatch(
          updateOrNewWatermark({
            userID: user?.user_id,
            groupID: groupId,
            watermark: {
              id: content.id,
              baseID: content.base_id,
              content: JSON.stringify(content),
              updateTime: ''
            },
            insInfo: {
              operateTemplateID: '',
              fromPlace: 'webWatermarkManage',
              isAutoCreateGroup: 0
            },
            usedItems: usedItems
          })
        )
      } else {
        const wm = produce(watermark, (draft) => {
          draft.id = content.id
          draft.updateTime = ''
          draft.createTime = time
          draft.content = JSON.stringify(content)
          draft.watermarkContent = content
          draft.isDraft = true
          draft.itemsList = watermark.itemsList.map((item) => {
            item.watermarkID = content.id
            return item
          })
        })
        setStorage(wm, groupId)
      }
    }

    clearOnCopyToOtherGroups()
    message.success('操作成功')
  }

  const clearOnCopyToOtherGroups = () => {
    setCopyToOtherVisible(false)
    setIsSubmit(false)
    setCopyToGroupsModalLoading(false)
    setSearchGroupName('')
    setTargetGroups([])
  }

  const menuProps = {
    onClick: onMenuClick,
    style: { width: '120px' },
    items: [
      {
        key: '1',
        label: <span style={{ color: '#0094FF' }}>复制到本团队</span>
      },
      { type: 'divider' as const },
      {
        key: '2',
        label: <span style={{ color: '#0094FF' }}>复制到其他团队</span>
      }
    ]
  }

  return (
    <span>
      <Dropdown key="copy" menu={menuProps} disabled={disabled}>
        <Button style={{ paddingLeft: 0 }} type="link" disabled={disabled}>
          复制
        </Button>
      </Dropdown>
      <Modal
        title="复制水印"
        open={copyModalVisible}
        onCancel={() => setCopyModalVisible(false)}
        onOk={onCopy}
        okButtonProps={{ disabled: !name || !name.trim() }}
      >
        <ul>
          <li>复制的水印模板不会直接发布。</li>
          <li>您可以在复制的水印模板编辑后，选择是否发布。</li>
        </ul>
        <Input
          placeholder="请输入水印名称"
          value={name}
          maxLength={12}
          onChange={(e) => setName(e.target.value)}
        />
      </Modal>
      <Modal
        width={400}
        styles={{ body: { paddingBottom: 10 } }}
        title="复制到其他团队"
        open={copyToOtherVisible}
        onCancel={clearOnCopyToOtherGroups}
        footer={[
          isGroupSelectedAll ? (
            <Button
              key={0}
              style={{ float: 'left' }}
              type="link"
              onClick={() => {
                setTargetGroups([])
                setSearchGroupName('')
              }}
            >
              取消全选
            </Button>
          ) : (
            <Button
              key={0}
              style={{ float: 'left' }}
              type="link"
              onClick={() => {
                setTargetGroups(allowCopyGroupIds)
                setSearchGroupName('')
              }}
            >
              选择全部
            </Button>
          ),
          <Button key={1} onClick={clearOnCopyToOtherGroups}>
            取消
          </Button>,
          <Button
            key={2}
            type="primary"
            loading={copyToGroupsModalLoading}
            disabled={targetGroups.length === 0}
            onClick={onCopyToOtherGroups}
          >
            确定
          </Button>
        ]}
      >
        <Search
          placeholder="请搜索团队名称"
          enterButton
          value={searchGroupName}
          onChange={(e) => setSearchGroupName(e.target.value)}
        />
        <Checkbox.Group
          style={{
            marginTop: '20px',
            width: '100%',
            height: '208px',
            overflowY: 'auto',
            display: 'block'
          }}
          value={targetGroups}
          onChange={(checkedValues) => {
            const groupIDs: string[] = checkedValues.map((value) =>
              String(value)
            )
            setTargetGroups(groupIDs)
          }}
        >
          {(user?.groups || [])
            .filter(
              (group: any) =>
                group.group_name?.includes(searchGroupName) &&
                group.group_id !== groupID &&
                group.role_type > CONFIG.memberRoleTypes.NORMAL
            )
            .map((group: any, index: number) => (
              <Row key={index} gutter={[16, 16]}>
                <Col span={24}>
                  <Checkbox value={group.group_id}>
                    {group.group_name}
                  </Checkbox>
                </Col>
              </Row>
            ))}
        </Checkbox.Group>
        <Divider style={{ margin: '12px 0' }} />
        <Checkbox
          checked={isSubmit}
          onChange={(e) => setIsSubmit(e.target.checked)}
        >
          复制并直接推送给团队成员
        </Checkbox>
      </Modal>
    </span>
  )
}

export default CopyButton
